import React, { useState } from 'react';
import { User, LogIn } from 'lucide-react';
import { useLanguage } from '../../hooks/useLanguage';

const AuthScreen = ({ onSignIn, onSignUp, onReset }) => {
  const [isSignUp, setIsSignUp] = useState(false);
  const { t } = useLanguage();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [info , setInfo ] = useState(''); 

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      if (isSignUp) {
        if (password !== confirm) {
          setError(t('passwordMismatch'));
          return;
        }
        await onSignUp(email, password);
      } else {
        await onSignIn(email, password);
      }
    } catch (error) {
      setError(getErrorMessage(error.code));
    } finally {
      setLoading(false);
    }
  };

  const getErrorMessage = (errorCode) => {
    switch (errorCode) {
      case 'auth/user-not-found':
        return t('userNotFound');
      case 'auth/wrong-password':
        return t('wrongPassword');
      case 'auth/email-already-in-use':
        return t('emailInUse');
      case 'auth/weak-password':
        return t('weakPassword');
      case 'auth/invalid-email':
        return t('invalidEmail');
      default:
        return t('errorOccurred') + ': ' + errorCode;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center p-4">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl p-8 w-full max-w-md">
        <div className="text-center mb-8">
          <h1 className="inline-flex items-center justify-center text-3xl font-bold text-gray-800 dark:text-gray-100 mb-2">
            <img
              src="/favicon.png"
              alt="AniVal"
              className="w-8 h-8 mr-2"   /* ← 右側に少し余白を付ける */
            />
            AniVal
          </h1>

          <p className="text-gray-600 dark:text-gray-300">
            {t('appDescription')}
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {t('email')}
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
              placeholder="your-email@example.com"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {t('password')}
            </label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
              placeholder={t('passwordMinLength')}
              minLength="6"
              required
            />
          </div>

          {error && (
            <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-700 text-red-700 dark:text-red-400 px-4 py-3 rounded-lg">
              {error}
            </div>
          )}

          {info && (
            <div className="bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-700 text-green-700 dark:text-green-400 px-4 py-3 rounded-lg">
              {info}
            </div>
          )}

          {isSignUp && (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('confirmPassword')}
              </label>
              <input
                type="password"
                value={confirm}
                onChange={e => setConfirm(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-lg focus:ring-2 focus:ring-blue-500"
                minLength="6"
                required
              />
            </div>
          )}

          {!isSignUp && (
            <div className="text-center mb-4">
              <button
                type="button"
                onClick={async () => {
                  setError(''); setInfo('');
                  if (!email) { setError(t('enterEmailFirst')); return; }
                  try {
                    await onReset(email);
                    setInfo(t('resetEmailSent'));
                  } catch (e) {
                    setError(t('emailSendFailed') + ': ' + e.code);
                  }
                }}
                className="text-sm text-blue-600 dark:text-blue-400 hover:underline"
              >
                {t('forgotPassword')}
              </button>
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 disabled:from-gray-400 disabled:to-gray-400 text-white py-3 px-4 rounded-lg transition-all duration-200 flex items-center justify-center gap-2 shadow-lg"
          >
            {loading ? (
              <>
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                {t('processing')}
              </>
            ) : (
              <>
                {isSignUp ? <User size={20} /> : <LogIn size={20} />}
                {isSignUp ? t('signUp') : t('signIn')}
              </>
            )}
          </button>
        </form>

        <div className="mt-6 text-center">
          <button
            onClick={() => setIsSignUp(!isSignUp)}
            className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 text-sm transition-colors"
          >
            {isSignUp ? t('alreadyHaveAccount') : t('createAccount')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AuthScreen;
